import React, { Fragment, SyntheticEvent } from "react";
import { Component } from "react";
import { Button, Dropdown, Form } from "semantic-ui-react";
import NumberFormat from "react-number-format";
import PhoneInput, { CountryData } from "react-phone-input-2";
import PhoneNumber from "awesome-phonenumber";
import {
    ObjectLiteral,
    parentDataRequiredToRegister,
    minYearsToRegister,
    customPhoneMasks,
} from "../../constants";
import "react-phone-input-2/lib/style.css";
import moment from "moment";
import _ from "lodash";
import { Props, registrationType } from "../propTypes";
import { childFormComponent } from "./types";
import classnames from "classnames";
import { ProgressIndicator } from "./ProgressIndicator";
import { withCookies } from "react-cookie";
import { ResetForm } from "./ResetForm";
// Change Details Form Component
/**
 * This component is used to allow the user to change any form details they entered. Any required fields
 * that should not be changed will be disabled.
 *
 * The campus, countries, and states values are being pulled from this.props.actions.Main....
 * Check these actions and the api calls set up within them if you run into any problems.
 *
 * We are using the Form and Button components from Semantic UI React Library.
 * For more information on these components can be found at https://react.semantic-ui.com/collections/form/
 * and https://react.semantic-ui.com/elements/button/
 *
 *
 * For phone numbers, we are using the PhoneInput component for international phone number formatting
 * (https://www.npmjs.com/package/react-phone-input-2) and the PhoneNumber component to validate
 * (https://www.npmjs.com/package/awesome-phonenumber)
 */

type State = {
    messagesToShow: ObjectLiteral;
    showValidation: boolean;
    formatData: CountryData | {};
    phoneInputContent: string;
    parentPhoneInputContent: string;
    campusFocused: boolean;
    tshirtSizeFocused: boolean;
    countryFocused: boolean;
    stateFocused: boolean;
};

class ChangeDetails extends Component<Props & childFormComponent, State> {
    state: State = {
        messagesToShow: [],
        showValidation: false,
        formatData: {},
        phoneInputContent: "+1",
        parentPhoneInputContent: "+1",
        campusFocused: false,
        tshirtSizeFocused: false,
        countryFocused: false,
        stateFocused: false,
    };

    // Component Did Mount Lifecycle Method
    /**
     * componentDidMount() runs immediately after the component mounts.
     */
    componentDidMount() {
        if (this.props.main.countries.length < 1) {
            this.props.actions.main.main_get_country_list();
        }

        if (this.props.main.states && this.props.main.states.length < 1) {
            this.props.actions.main.main_get_states_list();
        }

        if (this.props.main.campuses.length < 1) {
            this.props.actions.main.main_get_all_campuses();
        }

        if (
            this.props.registrationType === registrationType.Baptism &&
            this.props.actions.main.main_get_tshirt_size_list.length === 0
        ) {
            this.props.actions.main.main_get_tshirt_size_list();
        }

        document.body.classList.add("change-details");

        this.validateForm();
    }

    // Save and Continue Function
    /**
     * saveAndContinue() saves the values to the state and goes to the corresponding next step.
     */
    saveAndContinue = (e: SyntheticEvent) => {
        e.preventDefault();
        const {
            values: { campus, country },
        } = this.props;

        if (this.validateForm()) {
            if (!_.isEmpty(this.state.formatData)) {
                this.props.handleChange("phoneFormat")(
                    null,
                    this.state.formatData
                );
            }

            this.props.main.campuses.forEach((campusObj: ObjectLiteral) => {
                if (campus === campusObj.id) {
                    this.props.handleChange("campusName")(null, campusObj.name);
                }
            });

            this.props.main.countries.forEach((countryObj: ObjectLiteral) => {
                if (country === countryObj.Abbrev) {
                    this.props.handleChange("countryName")(
                        null,
                        countryObj.Country
                    );
                }
            });

            this.props.changeDetails && this.props.changeDetails();
        }
    };

    validateForm(
        showMessages: boolean = true,
        values = this.props.values
    ): boolean {
        const messagesToShow: ObjectLiteral = {};
        let validForm = false;
        const {
            address,
            city,
            state,
            phone,
            emailConfirmation,
            phoneFormat,
            phoneConfirmation,
            phoneConfirmationFormat,
            firstName,
            lastName,
            birthday,
            campus,
            country,
            email,
            parentEmail,
            parentFirstName,
            parentLastName,
            parentPhone,
            parentPhoneFormatData,
        } = values;

        let validPhone = false;

        if (
            phoneConfirmation &&
            !_.isEmpty(phoneConfirmationFormat) &&
            !emailConfirmation
        ) {
            const pn = new PhoneNumber(
                phoneConfirmation.replace(phoneConfirmationFormat.dialCode, ""),
                phoneConfirmationFormat.countryCode
            );

            validPhone = pn.isValid();
        } else if (phone && !_.isEmpty(phoneFormat)) {
            const pn = new PhoneNumber(
                phone.replace(phoneFormat.dialCode, ""),
                phoneFormat.countryCode
            );
            validPhone = pn.isValid();
        }

        if (
            birthday &&
            this.props.registrationType &&
            this.props.registrationType === registrationType.eGroup &&
            parentDataRequiredToRegister(moment(birthday))
        ) {
            if (!parentEmail) {
                messagesToShow["parentEmail"] = "Enter a parent's email.";
            } else if (parentEmail && !this.isValidEmail(parentEmail)) {
                messagesToShow["parentEmail"] = "Enter a parent's valid email.";
            }

            if (!parentFirstName) {
                messagesToShow["parentFirstName"] =
                    "Enter a parent's first name.";
            }

            if (!parentLastName) {
                messagesToShow["parentLastName"] =
                    "Enter a parent's last name.";
            }

            if (!parentPhone) {
                messagesToShow["parentPhone"] =
                    "Enter a parent's phone number.";
            } else {
                const pn = new PhoneNumber(
                    parentPhone.replace(parentPhoneFormatData.dialCode),
                    parentPhoneFormatData.countryCode
                );
                const validParentPhone = pn.isValid();

                if (!validParentPhone) {
                    messagesToShow["parentPhone"] =
                        "Enter a valid parent's phone number.";
                }
            }
        } else {
            const date = moment(birthday, "MM/DD/YYYY", true);
            const valid =
                date.isValid() &&
                date.isBefore(moment().subtract(minYearsToRegister, "years"));

            if (!valid) {
                if (!date.isValid()) {
                    messagesToShow[
                        "birthday"
                    ] = `Please enter a valid birthdate.`;
                } else if (
                    !date.isBefore(
                        moment().subtract(minYearsToRegister, "years")
                    )
                ) {
                    messagesToShow[
                        "birthday"
                    ] = `You have to be ${minYearsToRegister} or older to sign up`;
                }
            }
        }

        if (!emailConfirmation && !email) {
            messagesToShow["email"] = "Enter your email.";
        } else if (!emailConfirmation && email && !this.isValidEmail(email)) {
            messagesToShow["email"] = "Enter a valid email.";
        }

        if (!firstName) {
            messagesToShow["firstName"] = "Enter your first name.";
        }

        if (!lastName) {
            messagesToShow["lastName"] = "Enter your last name.";
        }

        if (!campus) {
            messagesToShow["campus"] = "Enter your campus.";
        }

        if (!country) {
            messagesToShow["country"] = "Enter your country.";
        }

        if (!address) {
            messagesToShow["address"] = "Enter your address.";
        }

        if (emailConfirmation && (!phone || (phone && !validPhone))) {
            messagesToShow["phone"] = "Enter a valid phone number.";
        }

        if (!city) {
            messagesToShow["city"] = "Enter your city.";
        }

        // if (zip) {
        //     validForm = true;
        // } else {
        //     messagesToShow['zip'] = "Enter the zip code"
        // }

        if (!country || (country === "US" && !state)) {
            messagesToShow["state"] = "Enter your state.";
        }

        if (showMessages) {
            this.setState({
                showValidation: !validForm,
                messagesToShow,
            });
        }

        return Object.keys(messagesToShow).length === 0;
    }

    // Handle Phone Input Change Function
    /**
     * handlePhoneInputChange() formats and validates the phone number input.
     */
    handlePhoneInputChange = (
        telNumber: any,
        data: CountryData,
        e,
        formattedPhoneNumber
    ) => {
        const pn = new PhoneNumber(formattedPhoneNumber);
        const valid = pn.isValid() || false;

        if (valid) {
            this.setState({ formatData: data, phoneInputContent: telNumber });
        } else {
            this.setState({
                phoneInputContent: telNumber,
            });
        }

        const fieldName = "phone";
        const newValues = Object.assign({}, this.props.values);
        newValues[fieldName] = telNumber;
        this.validateForm(true, newValues);

        this.props.handleChange(fieldName)(null, telNumber);
    };

    // Handle Email Change Function
    /**
     * handleEmailChange() handles the value of the email input and checks to make sure it's valid.
     */
    handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = "email";
        const newValues = Object.assign({}, this.props.values);
        newValues[fieldName] = e.target.value;
        this.validateForm(true, newValues);
        this.props.handleChange(fieldName)(e);
    };

    handleParentEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = "parentEmail";
        const newValues = Object.assign({}, this.props.values);
        newValues[fieldName] = e.target.value;
        this.validateForm(true, newValues);

        this.props.handleChange(fieldName)(e);
    };

    handleParentPhoneChange = (telNumber: any, format: CountryData) => {
        const fieldName = "parentPhone";
        const newValues = Object.assign({}, this.props.values);
        newValues[fieldName] = telNumber;
        newValues["parentPhoneFormatData"] = format;
        this.validateForm(true, newValues);

        this.setState({
            parentPhoneInputContent: telNumber,
        });

        this.props.handleChange(fieldName)(null, telNumber);
        this.props.handleChange("parentPhoneFormatData")(null, format);
    };

    isValidEmail = (email: string) => {
        const regex = RegExp(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
        const valid = regex.test(email);

        return valid;
    };

    onChangeField = (fieldName: string, event: any) => {
        const newValues = Object.assign({}, this.props.values);
        newValues[fieldName] = event.target.value;
        this.validateForm(true, newValues);
        this.props.handleChange(fieldName)(event);
    };

    render() {
        const {
            values: {
                phone,
                firstName,
                lastName,
                emailConfirmation,
                phoneConfirmation,
                email,
                birthday,
                country,
                address,
                city,
                state,
                zip,
                campus,
                phoneConfirmationFormat,
                parentFirstName,
                parentLastName,
                parentEmail,
                parentPhone,
                tshirtSize,
            },
        } = this.props;
        let validPhoneConfirmation = false;
        let phoneValue = phone;

        if (phoneConfirmation && phoneConfirmationFormat) {
            const pn = new PhoneNumber(
                phoneConfirmation.replace(phoneConfirmationFormat.dialCode, ""),
                phoneConfirmationFormat.countryCode
            );
            validPhoneConfirmation = pn.isValid();
        }

        if (validPhoneConfirmation) {
            phoneValue = phoneConfirmation;
        }

        return (
            <>
                <div className="col-xs-12 max-w-500 full-width center-content m-y-spacer-3xl">
                    <h2 className="text-dark m-b-spacer-none font-size-24 text-center">
                        Confirm Personal Info
                    </h2>
                    <Form className="p-y-spacer-xl phone-number-form">
                        <Form.Field
                            className={classnames(
                                "flex flex-column full-width m-b-spacer-lg ",
                                {
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["firstName"],
                                }
                            )}
                        >
                            <h4 className="text-dark sec-font text-dark m-y-spacer-md p-b-spacer-sm">
                                Your Info
                            </h4>
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: firstName,
                                    }
                                )}
                            >
                                <label>First Name</label>
                                <input
                                    autoFocus={true}
                                    className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                    onChange={(e) => {
                                        this.onChangeField("firstName", e);
                                    }}
                                    value={firstName}
                                />
                            </div>

                            {this.state.showValidation &&
                            this.state.messagesToShow["firstName"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["firstName"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg ",
                                {
                                    filled: lastName,
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["lastName"],
                                }
                            )}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: firstName,
                                    }
                                )}
                            >
                                <label>Last Name</label>
                                <input
                                    className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                    onChange={(e) => {
                                        this.onChangeField("lastName", e);
                                    }}
                                    value={lastName}
                                />
                            </div>
                            {this.state.showValidation &&
                            this.state.messagesToShow["lastName"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["lastName"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        <Form.Field
                            className={classnames(
                                "flex flex-column full-width m-b-spacer-lg ",
                                {
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["phone"],
                                }
                            )}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input with-phone-field rel-pos ",
                                    {
                                        filled: this.state.phoneInputContent,
                                    }
                                )}
                            >
                                <label className="pull-forward">
                                    Phone Number
                                </label>
                                {/* Disabling autofill for Chrome: readonly and onfocus attribute */}
                                {/* Disabling autofill for Safari: autoComplete attribute */}
                                <PhoneInput
                                    country="us"
                                    preferredCountries={["us"]}
                                    value={phoneValue}
                                    inputClass={`full-width`}
                                    placeholder=""
                                    inputProps={{
                                        id: "",
                                        autoFocus: false,
                                        autoComplete: "off",
                                    }}
                                    onChange={this.handlePhoneInputChange}
                                    disabled={validPhoneConfirmation}
                                    isValid={() =>
                                        !(
                                            !validPhoneConfirmation &&
                                            this.state.showValidation &&
                                            this.state.messagesToShow["phone"]
                                        )
                                    }
                                    masks={customPhoneMasks}
                                />
                            </div>
                            {!validPhoneConfirmation &&
                            this.state.showValidation &&
                            this.state.messagesToShow["phone"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["phone"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        <Form.Field
                            className={classnames(
                                "flex flex-column full-width m-b-spacer-lg ",
                                {
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["email"],
                                }
                            )}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: emailConfirmation || email,
                                    }
                                )}
                            >
                                <label>Email</label>
                                <input
                                    type="email"
                                    className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                    onChange={this.handleEmailChange}
                                    value={emailConfirmation || email}
                                    disabled={!!emailConfirmation}
                                />
                            </div>
                            {this.state.showValidation &&
                            this.state.messagesToShow["email"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["email"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        <Form.Field
                            className={classnames(
                                "rel-pos flex flex-column full-width m-b-spacer-lg ",
                                {
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["birthday"],
                                }
                            )}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: birthday,
                                    }
                                )}
                            >
                                <label>Birthday</label>
                                <NumberFormat
                                    autoFocus={true}
                                    format="##/##/####"
                                    placeholder="MM/DD/YYYY"
                                    value={birthday}
                                    className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                    onChange={(e: SyntheticEvent) => {
                                        this.onChangeField("birthday", e);
                                    }}
                                    alwaysShowMask={false}
                                />
                            </div>

                            {this.state.showValidation &&
                            this.state.messagesToShow["birthday"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["birthday"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        {this.props.registrationType ===
                        registrationType.Baptism ? (
                            <Form.Field
                                className={classnames(
                                    "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg",
                                    {
                                        filled:
                                            tshirtSize ||
                                            this.state.tshirtSizeFocused,
                                    }
                                )}
                            >
                                <label style={{ zIndex: 2 }}>
                                    T-Shirt Size
                                </label>
                                <Dropdown
                                    fluid
                                    selection
                                    options={this.props.main.tshirtSizes.map(
                                        (size) => {
                                            return {
                                                key: size,
                                                text: size,
                                                value: size,
                                            };
                                        }
                                    )}
                                    onChange={(
                                        event: SyntheticEvent,
                                        data: ObjectLiteral
                                    ) => {
                                        this.props.handleChange("tshirtSize")(
                                            undefined,
                                            data.value
                                        );

                                        if (!data.value) {
                                            this.setState({
                                                tshirtSizeFocused: false,
                                            });
                                        }
                                    }}
                                    onClick={() => {
                                        this.setState({
                                            tshirtSizeFocused: true,
                                        });
                                    }}
                                    onBlur={() => {
                                        this.setState({
                                            tshirtSizeFocused: false,
                                        });
                                    }}
                                    disabled={
                                        this.props.main.tshirtSizes.length <
                                            1 || this.props.main.isLoading
                                    }
                                    onFocus={(e) => {
                                        (e.target as any).setAttribute(
                                            "autocomplete",
                                            "nope"
                                        );
                                    }}
                                    value={tshirtSize}
                                    className="p-x-spacer-md ec-border-radius-sm b-width-xs text-alt-dark-grey full-width"
                                    name="tshirtSize"
                                />
                            </Form.Field>
                        ) : null}

                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg",
                                {
                                    filled: campus || this.state.campusFocused,
                                }
                            )}
                        >
                            <label style={{ zIndex: 2 }}>Primary Campus</label>
                            <Dropdown
                                clearable
                                fluid
                                search
                                selection
                                selectOnBlur={false}
                                selectOnNavigation={false}
                                options={[
                                    {
                                        key: "dummy-option-fix-autoselection",
                                        text: "",
                                        value: "",
                                        className: "hide",
                                    },
                                    ...this.props.main.campuses
                                        .filter((campus) => {
                                            return campus.active;
                                        })
                                        .map((campus) => {
                                            return {
                                                key: campus.id,
                                                text: campus.name,
                                                value: campus.id,
                                            };
                                        }),
                                ]}
                                onChange={(
                                    event: SyntheticEvent,
                                    data: ObjectLiteral
                                ) => {
                                    this.props.handleChange("campus")(
                                        undefined,
                                        data.value
                                    );

                                    if (!data.value) {
                                        this.setState({ campusFocused: false });
                                    }
                                }}
                                onClick={() => {
                                    this.setState({ campusFocused: true });
                                }}
                                onBlur={() => {
                                    this.setState({ campusFocused: false });
                                }}
                                disabled={
                                    this.props.main.campuses.length < 1 ||
                                    this.props.main.isLoading
                                }
                                onFocus={(e) => {
                                    (e.target as any).setAttribute(
                                        "autocomplete",
                                        "nope"
                                    );
                                }}
                                value={campus}
                                className="p-x-spacer-md ec-border-radius-sm b-width-xs text-alt-dark-grey full-width"
                                name="campus"
                            />
                        </Form.Field>

                        <h4 className="text-dark sec-font  text-dark m-y-spacer-md">
                            Address
                        </h4>
                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-y-spacer-lg",
                                {
                                    filled:
                                        country || this.state.countryFocused,
                                }
                            )}
                        >
                            <label style={{ zIndex: 2 }}>Country</label>
                            <Dropdown
                                clearable
                                fluid
                                search
                                selection
                                selectOnBlur={false}
                                selectOnNavigation={false}
                                options={[
                                    {
                                        key: "dummy-option-fix-autoselection",
                                        text: "",
                                        value: "",
                                        className: "hide",
                                    },
                                    ...this.props.main.countries.map(
                                        (country) => {
                                            return {
                                                key: country.Abbrev,
                                                text: country.Country,
                                                value: country.Abbrev,
                                                flag: country.Abbrev.toLowerCase(),
                                            };
                                        }
                                    ),
                                ]}
                                onChange={(
                                    event: SyntheticEvent,
                                    data: ObjectLiteral
                                ) => {
                                    this.props.handleChange("country")(
                                        undefined,
                                        data.value
                                    );

                                    if (!data.value) {
                                        this.setState({
                                            countryFocused: false,
                                        });
                                    }
                                }}
                                onClick={() => {
                                    this.setState({ countryFocused: true });
                                }}
                                onBlur={() => {
                                    this.setState({ countryFocused: false });
                                }}
                                disabled={
                                    this.props.main.countries.length < 1 ||
                                    this.props.main.isLoading
                                }
                                onFocus={(e) => {
                                    (e.target as any).setAttribute(
                                        "autocomplete",
                                        "nope"
                                    );
                                }}
                                value={country}
                                className="p-x-spacer-md ec-border-radius-sm b-width-xs text-alt-dark-grey full-width"
                                name="country"
                            />
                        </Form.Field>

                        <Form.Field
                            className={classnames(
                                "flex flex-column full-width m-b-spacer-lg ",
                                {
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["address"],
                                }
                            )}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: address,
                                    }
                                )}
                            >
                                <label>Address</label>
                                <input
                                    className="p-x-spacer-lg ec-border-radius-sm b-width-xs text-alt-dark-grey full-width"
                                    onChange={this.props.handleChange(
                                        "address"
                                    )}
                                    defaultValue={address}
                                    maxLength={100}
                                />
                            </div>

                            {this.state.showValidation &&
                            this.state.messagesToShow["address"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["address"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        <Form.Field
                            className={classnames(
                                "flex flex-column full-width m-b-spacer-lg ",
                                {
                                    "ec-error":
                                        this.state.showValidation &&
                                        this.state.messagesToShow["city"],
                                }
                            )}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: city,
                                    }
                                )}
                            >
                                <label>City</label>
                                <input
                                    className="p-x-spacer-lg ec-border-radius-sm b-width-xs text-alt-dark-grey full-width"
                                    onChange={this.props.handleChange("city")}
                                    defaultValue={city}
                                    maxLength={100}
                                />
                            </div>

                            {this.state.showValidation &&
                            this.state.messagesToShow["city"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["city"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        {country === "US" ? (
                            <Form.Field
                                className={classnames(
                                    "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg",
                                    {
                                        filled:
                                            state || this.state.stateFocused,
                                    }
                                )}
                            >
                                <label style={{ zIndex: 2 }}>State</label>

                                <Dropdown
                                    clearable
                                    fluid
                                    search
                                    selection
                                    selectOnBlur={false}
                                    selectOnNavigation={false}
                                    options={[
                                        {
                                            key: "dummy-option-fix-autoselection",
                                            text: "",
                                            value: "",
                                            className: "hide",
                                        },
                                        ...this.props.main.states?.map(
                                            (state: ObjectLiteral) => {
                                                return {
                                                    key: state.Abbrev,
                                                    text: state.State,
                                                    value: state.Abbrev,
                                                };
                                            }
                                        ),
                                    ]}
                                    onChange={(
                                        event: SyntheticEvent,
                                        data: ObjectLiteral
                                    ) => {
                                        this.props.handleChange("state")(
                                            undefined,
                                            data.value
                                        );

                                        if (!data.value) {
                                            this.setState({
                                                stateFocused: false,
                                            });
                                        }
                                    }}
                                    onClick={() => {
                                        this.setState({ stateFocused: true });
                                    }}
                                    onBlur={() => {
                                        this.setState({ stateFocused: false });
                                    }}
                                    disabled={
                                        (this.props.main.states &&
                                            this.props.main.states.length <
                                                1) ||
                                        this.props.main.isLoading
                                    }
                                    onFocus={(e) => {
                                        (e.target as any).setAttribute(
                                            "autocomplete",
                                            "nope"
                                        );
                                    }}
                                    value={state}
                                    className="p-x-spacer-md ec-border-radius-sm b-width-xs text-alt-dark-grey full-width"
                                    name="state"
                                />
                            </Form.Field>
                        ) : null}

                        <Form.Field
                            className={`m-b-spacer-md  ${
                                this.state.showValidation &&
                                this.state.messagesToShow["zip"]
                                    ? "ec-error"
                                    : ""
                            }`}
                        >
                            <div
                                className={classnames(
                                    "label-animation-input rel-pos",
                                    {
                                        filled: zip,
                                    }
                                )}
                            >
                                <label>Zip Code</label>
                                <input
                                    className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                    type="text"
                                    onChange={(e) => {
                                        this.onChangeField("zip", e);
                                    }}
                                    defaultValue={zip}
                                />
                            </div>
                            {this.state.showValidation &&
                            this.state.messagesToShow["zip"] ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.messagesToShow["zip"]}
                                </small>
                            ) : null}
                        </Form.Field>

                        {this.props.registrationType &&
                        this.props.registrationType ===
                            registrationType.eGroup &&
                        parentDataRequiredToRegister(moment(birthday)) ? (
                            <Fragment>
                                <h4 className="text-dark sec-font text-dark m-b-spacer-md m-t-spacer-lg p-b-spacer-md">
                                    Parent/Guardian Info
                                </h4>
                                <Form.Field
                                    className={classnames(
                                        "flex flex-column full-width m-b-spacer-lg",
                                        {
                                            "ec-error":
                                                this.state.showValidation &&
                                                this.state.messagesToShow[
                                                    "parentFirstName"
                                                ],
                                        }
                                    )}
                                >
                                    <div
                                        className={classnames(
                                            "label-animation-input rel-pos",
                                            {
                                                filled: parentFirstName,
                                            }
                                        )}
                                    >
                                        <label>First Name</label>
                                        <input
                                            className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                            onChange={(e) => {
                                                this.onChangeField(
                                                    "parentFirstName",
                                                    e
                                                );
                                            }}
                                            value={parentFirstName}
                                            maxLength={50}
                                        />
                                    </div>
                                    {this.state.showValidation &&
                                    this.state.messagesToShow[
                                        "parentFirstName"
                                    ] ? (
                                        <small className="ec-primary-font ec-line-height">
                                            {
                                                this.state.messagesToShow[
                                                    "parentFirstName"
                                                ]
                                            }
                                        </small>
                                    ) : null}
                                </Form.Field>

                                <Form.Field
                                    className={classnames(
                                        "flex flex-column full-width m-b-spacer-lg",
                                        {
                                            "ec-error":
                                                this.state.showValidation &&
                                                this.state.messagesToShow[
                                                    "parentLastName"
                                                ],
                                        }
                                    )}
                                >
                                    <div
                                        className={classnames(
                                            "label-animation-input rel-pos",
                                            {
                                                filled: parentLastName,
                                            }
                                        )}
                                    >
                                        <label>Last Name</label>
                                        <input
                                            className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                            onChange={(e) => {
                                                this.onChangeField(
                                                    "parentLastName",
                                                    e
                                                );
                                            }}
                                            value={parentLastName}
                                            maxLength={50}
                                        />
                                    </div>
                                    {this.state.showValidation &&
                                    this.state.messagesToShow[
                                        "parentLastName"
                                    ] ? (
                                        <small className="ec-primary-font ec-line-height">
                                            {
                                                this.state.messagesToShow[
                                                    "parentLastName"
                                                ]
                                            }
                                        </small>
                                    ) : null}
                                </Form.Field>
                                <Form.Field
                                    className={classnames(
                                        "flex flex-column full-width m-b-spacer-lg",
                                        {
                                            "ec-error":
                                                this.state.showValidation &&
                                                this.state.messagesToShow[
                                                    "parentEmail"
                                                ],
                                        }
                                    )}
                                >
                                    <div
                                        className={classnames(
                                            "label-animation-input rel-pos",
                                            {
                                                filled: parentEmail,
                                            }
                                        )}
                                    >
                                        <label>Email</label>
                                        <input
                                            className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                            type="email"
                                            onChange={
                                                this.handleParentEmailChange
                                            }
                                            value={parentEmail}
                                            maxLength={50}
                                        />
                                    </div>
                                    {this.state.showValidation &&
                                    this.state.messagesToShow["parentEmail"] ? (
                                        <small className="ec-primary-font ec-line-height">
                                            {
                                                this.state.messagesToShow[
                                                    "parentEmail"
                                                ]
                                            }
                                        </small>
                                    ) : null}
                                </Form.Field>

                                <Form.Field
                                    className={classnames(
                                        "flex flex-column full-width m-b-spacer-lg ",
                                        {
                                            "ec-error":
                                                this.state.showValidation &&
                                                this.state.messagesToShow[
                                                    "parentPhone"
                                                ],
                                        }
                                    )}
                                >
                                    <div
                                        className={classnames(
                                            "label-animation-input with-phone-field rel-pos ",
                                            {
                                                filled: this.state
                                                    .parentPhoneInputContent,
                                            }
                                        )}
                                    >
                                        <label className="pull-forward">
                                            Phone Number
                                        </label>
                                        <PhoneInput
                                            country="us"
                                            preferredCountries={["us"]}
                                            value={parentPhone}
                                            inputClass={`full-width ${
                                                this.state.showValidation &&
                                                this.state.messagesToShow[
                                                    "parentPhone"
                                                ]
                                                    ? "ec-error"
                                                    : ""
                                            }`}
                                            inputProps={{
                                                id: "",
                                                autoFocus: false,
                                                autoComplete: "off",
                                            }}
                                            onChange={
                                                this.handleParentPhoneChange
                                            }
                                            isValid={() =>
                                                !(
                                                    this.state.showValidation &&
                                                    this.state.messagesToShow[
                                                        "parentPhone"
                                                    ]
                                                )
                                            }
                                            masks={customPhoneMasks}
                                        />
                                    </div>
                                    {this.state.showValidation &&
                                    this.state.messagesToShow["parentPhone"] ? (
                                        <small className="ec-primary-font ec-line-height">
                                            {
                                                this.state.messagesToShow[
                                                    "parentPhone"
                                                ]
                                            }
                                        </small>
                                    ) : null}
                                </Form.Field>
                            </Fragment>
                        ) : null}

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                className="full-width no-border text-white  p-y-spacer-md uppercase let-sp-reg hover-grow ec-border-radius"
                                onClick={this.saveAndContinue}
                                disabled={!this.validateForm(false)}
                                id={`${this.props.tagPrefixId}-change-details-next`}
                            >
                                Next
                            </Button>
                        )}
                    </Form>
                    <div className="m-t-spacer-lg">
                        <ProgressIndicator currentStep={9} />
                    </div>
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-change-details`}
                    cancelUrl={this.props.cancelUrl || ""}
                />
            </>
        );
    }
}

export default withCookies(ChangeDetails);
